import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUserPrefs } from '../contexts/UserPrefsContext';
import { useHotkeys } from '../hooks/useHotkeys';
import { ShortcutsDialog } from './ShortcutsDialog';
import { Logo } from './Logo';
import { Footer } from './Footer';
import { flushQueue, queueLength, onOnline, isOnline } from '../lib/offlineQueue';
import { toast } from 'sonner';
import {
  Calendar, ListChecks, BarChart3, Sparkles, Settings as SettingsIcon,
  Sun, Moon, LogOut, WifiOff, Keyboard, CalendarDays,
} from 'lucide-react';

const navItems = [
  { to: '/today', label: 'Today', icon: Calendar, testid: 'nav-today' },
  { to: '/habits', label: 'Habits', icon: ListChecks, testid: 'nav-habits' },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays, testid: 'nav-calendar' },
  { to: '/stats', label: 'Stats', icon: BarChart3, testid: 'nav-stats' },
  { to: '/templates', label: 'Templates', icon: Sparkles, testid: 'nav-templates' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, testid: 'nav-settings' },
];

const linkClass = ({ isActive }) =>
  `group flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-primary text-primary-foreground'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
  }`;

export const Layout = () => {
  const { signOut, user } = useAuth();
  const { theme, toggle } = useTheme();
  const { prefs } = useUserPrefs();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(queueLength());

  // Where "go home" lands the user (logo click + hotkey).
  const homePath = prefs.home_screen === 'welcome' ? '/home' : '/today';

  // Global hotkeys
  useHotkeys({
    'n': () => { if (window.location.pathname === '/today') window.dispatchEvent(new CustomEvent('mfh:new-habit')); },
    'g t': () => navigate('/today'),
    'g h': () => navigate('/habits'),
    'g c': () => navigate('/calendar'),
    'g s': () => navigate('/stats'),
    'g r': () => navigate('/templates'),
    'g ,': () => navigate('/settings'),
    '?': () => setHelpOpen(true),
  }, [navigate]);

  // Online/offline + auto-flush queue
  useEffect(() => {
    const offHandler = () => setOnline(false);
    const onHandler = async () => {
      setOnline(true);
      const len = queueLength();
      if (len > 0) {
        const { ok, fail } = await flushQueue();
        setPending(queueLength());
        if (ok > 0) toast.success(`Synced ${ok} log${ok > 1 ? 's' : ''}`);
        if (fail > 0) toast.error(`${fail} log${fail > 1 ? 's' : ''} failed to sync`);
        window.dispatchEvent(new CustomEvent('mfh:queue-flushed'));
      }
    };
    window.addEventListener('offline', offHandler);
    window.addEventListener('online', onHandler);
    const tick = setInterval(() => setPending(queueLength()), 2000);
    return () => {
      window.removeEventListener('offline', offHandler);
      window.removeEventListener('online', onHandler);
      clearInterval(tick);
    };
  }, []);

  const onLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-border bg-card/40 backdrop-blur-sm px-5 py-7 sticky top-0 h-screen" data-testid="desktop-sidebar">
        <Link
          to={homePath}
          className="text-primary inline-flex rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          aria-label="Go to home"
          data-testid="logo-link-desktop"
        >
          <Logo />
        </Link>
        <nav className="mt-10 flex flex-col gap-1.5" aria-label="Primary">
          {navItems.map((it) => (
            <NavLink key={it.to} to={it.to} className={linkClass} data-testid={it.testid}>
              <it.icon className="h-4 w-4" strokeWidth={1.75} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-border space-y-1">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="w-full flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            data-testid="shortcuts-btn"
          >
            <Keyboard className="h-4 w-4" strokeWidth={1.75} />
            Shortcuts
          </button>
          <button
            type="button"
            onClick={toggle}
            className="w-full flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            data-testid="theme-toggle-desktop"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-3 rounded-full px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            Sign out
          </button>
          {user && (
            <div className="px-4 pt-3 text-xs text-muted-foreground truncate" data-testid="current-user-email">
              {user.email}
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl pt-safe">
        <div className="flex items-center justify-between px-4 h-14">
          <Link
            to={homePath}
            className="text-primary inline-flex rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            aria-label="Go to home"
            data-testid="logo-link-mobile"
          >
            <Logo size={32} />
          </Link>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggle}
              className="p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Toggle theme"
              data-testid="theme-toggle-mobile"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" strokeWidth={1.75} /> : <Moon className="h-5 w-5" strokeWidth={1.75} />}
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Sign out"
              data-testid="logout-btn-mobile"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 md:ml-0 pt-14 md:pt-0 pb-20 md:pb-0 relative z-10">
        {(!online || pending > 0) && (
          <div className="sticky top-14 md:top-0 z-30 mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 pt-4" data-testid="offline-banner">
            <div className="rounded-full border border-border bg-secondary/10 text-secondary px-4 py-2 text-xs flex items-center gap-2">
              <WifiOff className="h-3.5 w-3.5" />
              {!online ? "You're offline — logs will sync when you're back." : `Syncing ${pending} pending change${pending > 1 ? 's' : ''}…`}
            </div>
          </div>
        )}
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 py-6 md:py-10">
          <Outlet />
        </div>
        <Footer className="mt-6" />
      </main>

      {/* Mobile bottom nav */}
      <nav
        aria-label="Primary"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl pb-safe"
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-6">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`
              }
              data-testid={`${it.testid}-mobile`}
            >
              <it.icon className="h-5 w-5" strokeWidth={1.75} />
              {it.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <ShortcutsDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
};
